import { useEffect, useState } from "react";
import styles from "./App.module.css";
import { FireBackground } from "./components/FireBackground";
import { PriceDisplay } from "./components/PriceDisplay";
import { BetSlider } from "./components/BetSlider";
import { BurgerMenu } from "./components/BurgerMenu";
import { RespawnScreen } from "./components/RespawnScreen";
import { OnboardingModal, isOnboarded } from "./components/OnboardingModal";
import { LeaderboardModal } from "./components/LeaderboardModal";
import { AutoBotModal } from "./components/AutoBotModal";
import { StreakToast } from "./components/StreakToast";
import { TasksModal } from "./components/modals/TasksModal";
import { FrensModal } from "./components/modals/FrensModal";
import { useGame, type BetSide } from "./store/game";
import { usePrice } from "./hooks/usePrice";
import { useHaptic } from "./hooks/useHaptic";
import { initTelegram } from "./telegram";

export function App() {
  const price = useGame((s) => s.price);
  const direction = useGame((s) => s.direction);
  const balance = useGame((s) => s.balanceUsd);
  const betAmount = useGame((s) => s.betAmount);
  const phase = useGame((s) => s.phase);
  const respawnAt = useGame((s) => s.respawnAt);
  const placeBet = useGame((s) => s.placeBet);
  const setBetAmount = useGame((s) => s.setBetAmount);
  const tickCountdown = useGame((s) => s.tickCountdown);
  const dismissResult = useGame((s) => s.dismissResult);
  const initSession = useGame((s) => s.initSession);

  const haptic = useHaptic();

  // Onboarding gate
  const [onboarded, setOnboarded] = useState(isOnboarded);

  usePrice();

  useEffect(() => {
    initTelegram();
    initSession();
  }, []); // intentional empty deps — fire once on mount

  // ── Sync direction + active-side onto <html> for CSS theme ───────────
  useEffect(() => {
    document.documentElement.dataset.direction = direction;
  }, [direction]);

  useEffect(() => {
    if (phase.kind === "pending") {
      document.documentElement.dataset.activeSide = phase.side;
    } else {
      delete document.documentElement.dataset.activeSide;
    }
  }, [phase]);

  // ── Countdown driver ──────────────────────────────────────────────────
  useEffect(() => {
    if (phase.kind !== "pending") return;
    const id = setInterval(() => {
      const cur = useGame.getState().phase;
      if (cur.kind !== "pending") { clearInterval(id); return; }
      tickCountdown();
    }, 1_000);
    return () => clearInterval(id);
  }, [phase.kind, tickCountdown]);

  // ── Auto-dismiss result after 2s ──────────────────────────────────────
  useEffect(() => {
    if (phase.kind !== "result") return;
    haptic.notify(
      phase.outcome === "win" ? "success" : phase.outcome === "loss" ? "error" : "warning",
    );
    const t = setTimeout(() => dismissResult(), 2_000);
    return () => clearTimeout(t);
  }, [phase, dismissResult, haptic]);

  const handleTap = (side: BetSide) => {
    if (phase.kind !== "idle" || price === null) { haptic.notify("error"); return; }
    if (betAmount > balance) { haptic.notify("error"); return; }
    haptic.impact("medium");
    void placeBet(side);
  };

  const activeSide = phase.kind === "pending" ? phase.side : null;
  const tapsDisabled = phase.kind !== "idle" || price === null || balance < 1;
  const isBurnedOut = balance < 1 && respawnAt !== null;

  return (
    <div className={styles.shell}>
      {/* Onboarding — blocks everything until done */}
      {!onboarded && <OnboardingModal onDone={() => setOnboarded(true)} />}

      {/* Respawn overlay — appears when balance = 0 */}
      {onboarded && isBurnedOut && <RespawnScreen />}

      <FireBackground
        direction={direction}
        activeSide={activeSide}
        onTap={handleTap}
        disabled={tapsDisabled}
      />

      <BurgerMenu />
      <TasksModal />
      <FrensModal />
      <LeaderboardModal />
      <AutoBotModal />
      <StreakToast />

      <div className={styles.priceLayer}>
        <PriceDisplay price={price} phase={phase} />
      </div>

      <div className={styles.bottomDock}>
        <BetSlider
          value={betAmount}
          balance={balance}
          onChange={setBetAmount}
          disabled={phase.kind !== "idle" || isBurnedOut}
        />
      </div>
    </div>
  );
}
